import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from '../../services/externalDataService.service';
import { SubscriptionService, PaginatedSubscriptions } from '../../services/subscription.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { UpdateSubscriptionComponent } from './update-subscription/update-subscription.component';
import { LoadingComponent } from '../shared/loading/loading.component';

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, PaginationComponent, UpdateSubscriptionComponent, LoadingComponent],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.css'
})
export class SubscriptionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Observables from service
  subscriptions$: Observable<PaginatedSubscriptions>;
  loading$: Observable<boolean>;
  
  // Local component state
  expandedRows: Set<string> = new Set();
  showUpdateModal = false;
  selectedSubscription: Subscription | null = null;
  showDetails = false;
  selectedSubscriptionForDetails: Subscription | null = null;
  currentSubscriptions: Subscription[] = [];
  
  // Filter properties (bound to template)
  statusFilter: string = '';
  zoneFilter: string = '';
  agentFilter: string = '';
  
  // Make Math available in template
  Math = Math;

  constructor(
    private subscriptionService: SubscriptionService
  ) {
    this.subscriptions$ = this.subscriptionService.paginatedSubscriptions$;
    this.loading$ = this.subscriptionService.loading$;
  }

  ngOnInit(): void {
    this.subscriptionService.initialize();
    
    // Subscribe to subscriptions data to keep local copy
    this.subscriptions$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data && data.items) {
        this.currentSubscriptions = data.items;
      }
    });
    
    // Subscribe to filter changes and apply them
    this.subscriptionService.filters$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(filters => {
      this.statusFilter = filters.status;
      this.zoneFilter = filters.zone;
      this.agentFilter = filters.agent;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Pagination methods
  get totalPages(): number {
    let totalCount = 0;
    let pageSize = 10;
    
    this.subscriptions$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      totalCount = data.totalCount;
      pageSize = data.pageSize;
    });
    
    return Math.ceil(totalCount / pageSize) || 1;
  }

  get currentPage(): number {
    let page = 1;
    this.subscriptionService.currentPage$.pipe(takeUntil(this.destroy$)).subscribe(p => page = p);
    return page;
  }

  get pageSize(): number {
    let size = 10;
    this.subscriptionService.pageSize$.pipe(takeUntil(this.destroy$)).subscribe(s => size = s);
    return size;
  }

  onPageChange(page: number): void {
    this.subscriptionService.setCurrentPage(page);
    this.expandedRows.clear(); // Clear expanded rows when changing pages
  }

  onPageSizeChange(newPageSize: number): void {
    this.subscriptionService.setPageSize(newPageSize);
    this.expandedRows.clear();
  }

  // View management
  toggleRow(subscriptionId: string): void {
    const subscription = this.currentSubscriptions.find(sub => sub.subscriptionId === subscriptionId);
    if (subscription) {
      this.showSubscriptionDetails(subscription);
    }
  }

  // Helper method to check if row is expanded (for future expandable row implementation)
  isRowExpanded(subscriptionId: string): boolean {
    return this.expandedRows.has(subscriptionId);
  }

  showSubscriptionDetails(subscription: Subscription): void {
    this.selectedSubscriptionForDetails = subscription;
    this.showDetails = true;
  }

  backToList(): void {
    this.showDetails = false;
    this.selectedSubscriptionForDetails = null;
    this.expandedRows.clear();
  }

  // Modal management
  openUpdateModal(subscription: Subscription): void {
    this.selectedSubscription = subscription;
    this.showUpdateModal = true;
  }

  closeUpdateModal(): void {
    this.showUpdateModal = false;
    this.selectedSubscription = null;
  }

  onSubscriptionUpdated(): void {
    this.closeUpdateModal();
    this.subscriptionService.refreshData();
  }

  // Filter methods
  applyFilters(): void {
    this.subscriptionService.setFilters({
      status: this.statusFilter,
      zone: this.zoneFilter,
      agent: this.agentFilter
    });
    this.expandedRows.clear();
  }

  clearFilters(): void {
    this.statusFilter = '';
    this.zoneFilter = '';
    this.agentFilter = '';
    this.subscriptionService.clearFilters();
    this.expandedRows.clear();
  }

  // Helper methods - delegate to service
  getStatusText(status: number): string {
    return this.subscriptionService.getStatusText(status);
  }

  getStatusClass(status: number): string {
    return this.subscriptionService.getStatusClass(status);
  }

  getTypeText(type: number): string {
    return this.subscriptionService.getTypeText(type);
  }

  getCadenceText(cadence: number): string {
    return this.subscriptionService.getCadenceText(cadence);
  }

  getPaymentStatusText(status: number): string {
    return this.subscriptionService.getPaymentStatusText(status);
  }

  getPaymentStatusClass(status: number): string {
    return this.subscriptionService.getPaymentStatusClass(status);
  }

  getPaymentMethodText(paymentMethod: number): string {
    return this.subscriptionService.getPaymentMethodText(paymentMethod);
  }

  getAgentName(agentId: string): string {
    return this.subscriptionService.getAgentName(agentId);
  }

  getZoneName(zoneId: string): string {
    return this.subscriptionService.getZoneName(zoneId);
  }

  // Utility methods
  formatDate(dateString: string): string {
    return this.subscriptionService.formatDate(dateString);
  }

  formatCurrency(amount: number, currency: string = 'INR'): string {
    return this.subscriptionService.formatCurrency(amount, currency);
  }



  // Payment information helpers
  getDiscountedAmount(subscription: Subscription): number {
    return subscription.baseAmount - subscription.discountAmount;
  }

  hasDiscount(subscription: Subscription): boolean {
    return subscription.discountAmount > 0 || subscription.discountPercentage > 0;
  }
}